import React, { useEffect, useState, useMemo } from "react";
import { setCurrentPageTitle } from "../../store/slices/pageSlice";
import { useDispatch, useSelector } from "react-redux";
// import AddIcon from "@mui/icons-material/Add";
// import RemoveIcon from "@mui/icons-material/Remove";
import {
  createSalesTax,
  deleteSalesTax,
  fetchSalesTaxes,
  updateSalesTax,
} from "../../store/slices/salesTaxesSlice";
import { useSnackbar } from "notistack";
import { MdSearch } from "react-icons/md";
import { fetchEmployees } from "../../store/slices/employeeSlice";
import { SquarePen, Trash2 } from "lucide-react";
import { CiCircleCheck } from "react-icons/ci";
import ConfirmationModal from "../../components/common/confirm/ConfirmationModal";
import { getFeatureAccessLevel } from "../../utils/featureAccessUtils";

const DEFAULT_TAXES = [
  { taxName: "G&A Expenses", taxPercentage: "15" },
  { taxName: "Tax", taxPercentage: "27" },
];

const GandATax = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [searchQuery, setSearchQuery] = useState("");
  const { employees } = useSelector((state) => state.employees);
  const { session } = useSelector((state) => state.session);
  const { taxes } = useSelector((state) => state.salesTaxes);

  const userLevel = session?.auth || (typeof localStorage !== "undefined" ? localStorage.getItem("auth") : "") || "";
  const currentEmployee = employees?.find(
    (emp) => (emp.EmpEmail || "").trim().toLowerCase() === ((typeof localStorage !== "undefined" ? localStorage.getItem("email") : "") || "").trim().toLowerCase()
  );
  const gaTaxAccessLevel = getFeatureAccessLevel(currentEmployee, "G&A AND TAX");
  const taxRatesAccessLevel = getFeatureAccessLevel(currentEmployee, "TAX RATES G&A TAX");
  const isL2NoAccessToGandATax =
    userLevel === "L2" && String(gaTaxAccessLevel || "").trim().toUpperCase() === "NO ACCESS";
  const hasNoFeatureConfig = !currentEmployee || !Array.isArray(currentEmployee?.featureAccess) || currentEmployee.featureAccess.length === 0;
  const isViewOnly = !!currentEmployee && !hasNoFeatureConfig && (
    (taxRatesAccessLevel && String(taxRatesAccessLevel).trim().toUpperCase() === "VIEW ONLY") ||
    (gaTaxAccessLevel && String(gaTaxAccessLevel).trim().toUpperCase() === "VIEW ONLY")
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [rowid, setRowToDelete] = useState(null);
  const [isModal, setIsModal] = useState(false);
  const [defaultRowToRemove, setDefaultRowToRemove] = useState(null);

  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [editingTaxId, setEditingTaxId] = useState(null);
  const [taxName, setTaxName] = useState("");
  const [taxPercentage, setTaxPercentage] = useState("");

  useEffect(() => {
    dispatch(setCurrentPageTitle("G&A Tax Rates"));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchSalesTaxes());
  // const [selectedrowid, setselectedrowid] = useState("");
    dispatch(fetchEmployees());
  }, [dispatch]);

  // Merge: show defaults + DB taxes; if DB has same name as default, show DB entry only. Use createdBy so only current user's taxes count.
  useEffect(() => {
    const userEmail = localStorage.getItem("email");
    const currentEmployee = employees.find((emp) => emp.EmpEmail === userEmail);
    const financeEmployeeIds = new Set(
      (employees || [])
        .filter((emp) => emp?.EmpAuthLevel === "L2")
        .map((emp) => emp?._id)
        .filter(Boolean)
    );
    const dbTaxes =
      currentEmployee != null
        ? (taxes || []).filter((tax) => {
            if (!tax?.createdBy) return false;
            if (userLevel === "L5") {
              return (
                tax.createdBy === currentEmployee._id ||
                financeEmployeeIds.has(tax.createdBy)
              );
            }
            return tax.createdBy === currentEmployee._id;
          })
        : [];
    const dbNamesLower = new Set(
      dbTaxes.map((t) => (t.taxName || "").trim().toLowerCase())
    );

    const merged = [];
    let rowId = Date.now();

    // Add defaults only when no DB entry matches by name
    DEFAULT_TAXES.forEach((def) => {
      const nameLower = (def.taxName || "").trim().toLowerCase();
      if (!dbNamesLower.has(nameLower)) {
        merged.push({
          id: rowId++,
          taxName: def.taxName,
          taxPercentage: def.taxPercentage,
          isNew: true,
        });
      }
    });

    // Add all DB entries (they override defaults when name matches)
    dbTaxes.forEach((tax) => {
      merged.push({
        id: rowId++,
        _id: tax._id,
        taxName: tax.taxName,
        taxPercentage: String(tax.taxPercentage ?? ""),
        isNew: false,
      });
    });

    setRows(merged);
    setAllRows(merged);
  }, [taxes, employees, userLevel]);

  // Filter rows based on search query
  const filteredRows = useMemo(() => {
    if (!searchQuery) return rows;

    const query = searchQuery.toLowerCase();
    return rows.filter(
      (row) =>
        row.taxName.toLowerCase().includes(query) ||
        row.taxPercentage.toString().includes(query)
    );
  }, [rows, searchQuery]);

  if (isL2NoAccessToGandATax) {
    return null;
  }

  // const handleRemoveRow = () => {
  //   if (!selectedrowid) {
  //     enqueueSnackbar("Please Select Row To Delete", { variant: "error" });
  //     return;
  //   }
  //   handleDeleteRow(selectedrowid);
  // };

  const handleDeleteRow = (row) => {
    if (isViewOnly) return;
    const id = row?._id;
    if (id) {
      setRowToDelete(id);
      setIsModalOpen(true);
    } else {
      setDefaultRowToRemove(row);
      setIsModal(true);
    }
  };

  const editTax = (tax) => {
    if (isViewOnly) return;
    setEditingTaxId(tax._id || tax.id);
    setTaxName(tax.taxName);
    setTaxPercentage(tax.taxPercentage.toString());
  };

  const handleAddTax = async (e) => {
    e.preventDefault();
    if (isViewOnly) return;
    if (!taxName || !taxPercentage) {
      enqueueSnackbar("Please fill all the fields!", { variant: "error" });
      return;
    }

    try {
      if (editingTaxId) {
        const taxToUpdate = rows.find(
          (r) => r._id === editingTaxId || r.id === editingTaxId
        );
        if (taxToUpdate?._id) {
          await dispatch(updateSalesTax({
            id: taxToUpdate._id,
            taxData: {
              taxName: taxName,
              taxPercentage: parseFloat(taxPercentage),
            },
          })).unwrap();
          enqueueSnackbar("Tax Updated Successfully!", { variant: "success" });
        } else {
          // Default tax (no _id): create in DB so it can be updated later
          await dispatch(createSalesTax({
            taxes: [{
              taxName: taxName,
              taxPercentage: parseFloat(taxPercentage),
        // Add new tax
            }],
          })).unwrap();
          enqueueSnackbar("Tax Added Successfully!", { variant: "success" });
        }
      } else {
        await dispatch(createSalesTax({
          taxes: [{
            taxName: taxName,
            taxPercentage: parseFloat(taxPercentage),
          }],
        })).unwrap();
        enqueueSnackbar("Tax Added Successfully!", { variant: "success" });
      }

      setTaxName("");
      setTaxPercentage("");
      setEditingTaxId(null);
      await dispatch(fetchSalesTaxes());
    } catch (error) {
      enqueueSnackbar(
        error?.error || "Something went wrong!",
        { variant: "error" }
      );
    }
  };

  const handleCancel = () => {
    setTaxName("");
    setTaxPercentage("");
    setEditingTaxId(null);
  };

  const confirmDelete = async () => {
    try {
      // Existing row - delete from backend
      await dispatch(deleteSalesTax(rowid)).unwrap();
      enqueueSnackbar("Row Deleted Successfully", {
        variant: "success",
      });
    } catch (error) {
      console.error("Delete failed:", error);
      enqueueSnackbar(error.message || "Failed to delete tax", {
        variant: "error",
      });
    } finally {
      dispatch(fetchSalesTaxes());
      // setselectedrowid("");
      setIsModalOpen(false);
    }
  };

  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (!query) {
      // If search query is empty, show all rows
      setRows(allRows);
      return;
    }

    // Filter rows based on search query
    const filtered = allRows.filter(
      (row) =>
        row.taxName.toLowerCase().includes(query.toLowerCase()) ||
        row.taxPercentage.toString().includes(query)
    );

    setRows(filtered);
  };

  return (
    <div className="bg-[#f3f8f8] pb-6 h-full">
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={confirmDelete}
      />
      <div className="relative flex justify-center align-middle items-center pt-2">
        <div className="flex flex-col relative w-[80%] gap-4">
          {/* Page Title */}
          <div className="flex items-center gap-2">
            <span className="w-2 h-6 bg-blue-600 rounded-full"></span>
            <h1 className="text-2xl font-semibold">Add Tax</h1>
          </div>

          <div className="flex gap-2">
            {/* Left Panel - Form */}
            <div className="w-[40%] bg-white rounded-md h-fit">
              <div className="flex px-3 py-4 gap-2 align-middle items-center border-b">
                <span className="w-2 h-6 bg-blue-600 rounded-full"></span>
                <h2 className="font-semibold text-xl">
                  Group Information
                </h2>
              </div>
              <div className="mainform px-3 py-2 flex flex-col gap-3">
                <div>
                  <div className='flex gap-1 pb-2 text-xs align-middle items-center font-semibold'>
                    Tax Name <span className="text-red-700 text-xs">*</span>
                  </div>
                  <div>
                    <input
                      required
                      disabled={isViewOnly}
                      className={`border w-full border-[#e5e7eb] rounded-md p-2 overflow-hidden outline-none text-xs ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : "bg-[#fcfcfc]"}`}
                      type="text"
                      placeholder="Enter Tax Name"
                      value={taxName}
                      onChange={(e) => {
                        if (isViewOnly) return;
                        const onlyLetters = e.target.value.replace(/[^a-zA-Z\s]/g, '');
                        setTaxName(onlyLetters);
                      }}
                    />
                  </div>
                </div>
                <div>
                  <div className='flex gap-1 pb-2 text-xs align-middle items-center font-semibold'>
                    % Tax Percentage
                  </div>
                  <div>
                    <input
                      disabled={isViewOnly}
                      className={`border w-full border-[#e5e7eb] rounded-md p-2 overflow-hidden outline-none text-xs ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : "bg-[#fcfcfc]"}`}
                      type="text"
                      placeholder="Enter Tax Percentage"
                      value={taxPercentage}
                      onChange={(e) => {
                        if (isViewOnly) return;
                        let newValue = e.target.value.replace(/[^0-9.]/g, "");
                        newValue = newValue.replace(/(\..*)\./g, "$1");
                        if (newValue !== "" && parseFloat(newValue) > 100) {
                          return;
                        }
                        setTaxPercentage(newValue);
                      }}
                    />
                  </div>
                </div>
                <div className="flex gap-2 text-xs">
                  <button
                    onClick={handleAddTax}
                    disabled={isViewOnly}
                    className={`p-1 flex align-middle items-center gap-2 px-4 overflow-hidden rounded-lg ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : "bg-[#0f61e6] hover:bg-blue-400 text-white"}`}
                  >
                    <CiCircleCheck size={16} />
                    {editingTaxId ? "Update Tax" : "Add Tax"}
                  </button>
                  <button
                    onClick={handleCancel}
                    className="py-2 px-3 border border-[#e5e7eb] bg-white overflow-hidden rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>

            {/* Right Panel - Table */}
            <div className="w-[60%] p-4 bg-white rounded-md">
              <div className="flex justify-between align-middle items-center py-2">
                <div className="flex items-center gap-3 shrink-0">
                  <span className="w-2 h-6 bg-blue-600 rounded-full"></span>
                  <h1 className="text-lg font-semibold text-gray-900">
                    Tax Directory
                  </h1>
                </div>

                <div className="flex gap-2 p-2 align-middle items-center border border-[#e5e7eb] rounded-lg">
                  <MdSearch className="w-5 h-5 text-gray-500" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchInputChange}
                    placeholder="Search Taxes"
                    className="w-full outline-none text-sm text-gray-900 placeholder-gray-500"
                  />
                </div>
              </div>
              <hr />
              <hr />
              <br />
              <div className="maintable min-h-[10rem] max-h-[60vh] overflow-auto">
                <table className="min-w-full divide-y divide-gray-200 border text-xs">
                  <thead className="bg-[#e8e8e8] sticky top-0 z-50 font-semibold">
                    <tr>
                      <th className="border p-2 text-start tracking-wider font-medium capitalize">Sr.no.</th>
                      <th className="border p-2 text-start tracking-wider font-medium capitalize">Tax Name</th>
                      <th className="border p-2 text-start tracking-wider font-medium capitalize">Tax Percentage</th>
                      <th className="border p-2 text-center tracking-wider font-medium capitalize">Actions</th>
                    </tr>
                  </thead>
                  <tbody className='bg-white divide-y divide-gray-200'>
                    {filteredRows.length > 0 ? (
                      filteredRows.map((row, index) => (
                        <tr className='border border-gray-400' key={index}>
                          <td className="px-2 border-b text-start py-2 text-black">{index + 1}</td>
                          <td className="px-2 text-start border-b py-2">{row.taxName}</td>
                          <td className="px-2 text-start border-b py-2">{row.taxPercentage}%</td>
                          <td className="px-2 border-b text-center py-2">
                            <div className="flex justify-center items-center gap-3">
                              <div
                                onClick={() => editTax(row)}
                                className={`${isViewOnly ? "text-[#9ca3af] cursor-not-allowed pointer-events-none opacity-60" : "cursor-pointer hover:opacity-70"}`}
                                title="Edit"
                              >
                                <SquarePen size={18} className={isViewOnly ? "text-[#9ca3af]" : "text-blue-500"} />
                              </div>
                              <div
                                onClick={() => handleDeleteRow(row)}
                                className={`${isViewOnly ? "text-[#9ca3af] cursor-not-allowed pointer-events-none opacity-60" : "cursor-pointer hover:opacity-70"}`}
                                title="Delete"
                              >
                                <Trash2 size={18} className={isViewOnly ? "text-[#9ca3af]" : "text-blue-500"} />
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center text-red-500 py-4">
                          {searchQuery.trim() === ''
                            ? 'No tax available'
                            : 'No matching tax found'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>


      {isModal && (
        <div className="fixed inset-0 flex items-center justify-center z-[1400] bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-[90%] max-w-md">
            <h2 className="text-xl font-semibold mb-4 text-center">
              Confirm Deletion
            </h2>
            <p className="text-gray-700 mb-6 text-center">
              Are you sure you want to delete this G&A and Tax Rates?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setIsModal(false)}
                className="px-4 py-2 rounded-md border border-gray-300 bg-gray-100 hover:bg-gray-200 text-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (defaultRowToRemove) {
                    const match = (r) =>
                      (r.id !== undefined && r.id === defaultRowToRemove.id) ||
                      (r._id && r._id === defaultRowToRemove._id);
                    setRows((prev) => prev.filter((r) => !match(r)));
                    setAllRows((prev) => prev.filter((r) => !match(r)));
                    setDefaultRowToRemove(null);
                  }
                  setIsModal(false);
                  enqueueSnackbar("Row removed from list.", {
                    variant: "success",
                  });
                }}
                className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white font-semibold"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GandATax;
      